import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { Tooltip } from 'primitives/tooltip'
import { CheckCircleIcon } from 'primitives/icons'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { Button } from '@repo/design-system-cf/components/button'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'

import { useClubhousesIsMemberQuery, useClubhousesJoinMutation } from '@web/gen/clubhouses'
import { LFS_URL } from '@web/utils/env'

export function PromotionSidebar({
  clubhouseSlug,
  clubhouseType,
  clubhouseName,
}: {
  clubhouseSlug: string
  clubhouseType: string
  clubhouseName?: string
}) {
  const t = useTranslations('pages.clubhouse')
  const queryClient = useQueryClient()
  const { openModal } = useUserAuthModal()
  const { data: isLoggedIn, isPending } = useIsLoggedInQuery()
  const joinMutation = useClubhousesJoinMutation()
  const user = useUser()
  const { data: isMember } = useClubhousesIsMemberQuery(
    { input: { clubhouseSlug } },
    {
      select: data => data?.clubhousesIsClubhouseMember.isMember,
    },
  )

  const handleClick = async () => {
    if (!isLoggedIn) {
      openModal('register')
      return
    }
    await joinMutation.mutateAsync(
      { input: { clubhouseSlug } },
      {
        onSuccess: data => {
          if (data?.clubhousesJoin?.Success) {
            sendAmplitudeEvent(AmplitudeEvents.JOINED_CLUBHOUSE, {
              title: clubhouseName,
            })
            queryClient.invalidateQueries({
              queryKey: ['clubhousesIsMember', { input: { clubhouseSlug } }],
            })
            queryClient.invalidateQueries({
              queryKey: ['clubhousesFromUser', { input: { userId: user.data?.id } }],
            })
          }
        },
      },
    )
  }

  return (!isPending && !isLoggedIn) || (isLoggedIn && !isMember) ? (
    <section
      className="bg-cf-white mt-4"
      style={{
        backgroundImage: `
        url(${LFS_URL}/web/pages/clubhouses/promotion-bg-top-left-purple.png),
        url(${LFS_URL}/web/pages/clubhouses/promotion-bg-bottom-right-purple.png)
      `,
        backgroundRepeat: 'no-repeat, no-repeat',
        backgroundPosition: 'top left, bottom right',
        backgroundSize: 'auto 50%, auto 50%',
      }}
    >
      <section className="rounded-[12px] border-legacy-cf-purple-100 w-full overflow-hidden border-2 p-4">
        <div className="flex flex-row items-center gap-1">
          <h2 className="text-16 font-semibold">{clubhouseName} Clubhouse</h2>
          <Tooltip
            value={
              <div className="text-cf-white bg-cf-black text-12 rounded-2 max-w-[200px] p-3  text-center shadow-md ">
                {t('sidebar.tooltip')}
              </div>
            }
            arrowClassName="fill-cf-black"
            delay={0}
            side="bottom"
            hideArrow={false}
            className="p-0"
            avoidCollisions
          >
            <div className="size-3">
              <CheckCircleIcon className="text-legacy-cf-purple-100 size-3 " />
            </div>
          </Tooltip>
        </div>
        <p className="text-14 text-gray-200">{clubhouseType}</p>
        <p className="text-14 text-cf-sf-gray-1050 pt-3">
          {t('sidebar.join_for_free_text', { clubhouse: clubhouseName ?? '' })}
        </p>
        <Button
          onClick={handleClick}
          className="mb-1 mt-4 w-full text-center"
          data-testid="clubhouse-promotion-join-button"
        >
          {t('sidebar.join_for_free')}
        </Button>
      </section>
    </section>
  ) : null
}
